import { type ComponentType, useState } from 'react'
import { createLogger } from '@ondit/logger'
import {
  ChipModal,
  ChipModalBody,
  ChipModalError,
  ChipModalField,
  ChipModalFooter,
  ChipModalHeader,
  SecretInput,
} from '@ondit/ui'
import { isApiClientError } from '@/lib/api/client/errors'
import {
  resourceScopeFields,
  resourceScopeFromOwner,
  resourceScopeKey,
} from '@/lib/core/resource-scope'
import {
  getTokenServiceAccountErrorMessage,
  type TokenServiceAccountDescriptor,
  type TokenServiceAccountField,
  type TokenServiceAccountFieldId,
} from '@/lib/credentials/token-service-accounts/descriptors'
import { withBrandIcon } from '@/blocks/brand-icon'
import {
  useCreateScopedCredential,
  useUpdateScopedCredential,
} from '@/hooks/queries/scoped-credentials'
import { useT } from '~/core/i18n'

const logger = createLogger('TokenServiceAccountModal')

function normalizeDomainInput(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/[/?#].*$/, '')
}

function normalizeField(field: TokenServiceAccountField, raw: string): string {
  return field.id === 'domain' && !field.keepPath ? normalizeDomainInput(raw) : raw.trim()
}

function openDocs(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

interface TokenServiceAccountModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId?: string
  organizationId?: string
  descriptor: TokenServiceAccountDescriptor
  serviceName: string
  serviceIcon: ComponentType<{ className?: string }>
  /** When set, reconnect (rotate the secret on) this credential in place. */
  credentialId?: string
  initialDisplayName?: string
  initialDescription?: string
  /** Called with the credential id after a successful create or reconnect. */
  onCreated?: (credentialId: string) => void
}

/**
 * Generic connect modal for token-paste service accounts. Renders the fields
 * declared by the provider's {@link TokenServiceAccountDescriptor}, in the
 * order it declares them, and submits through the same create/update
 * credential mutations as the other service-account modals. Server-side
 * verification failures are mapped from the route's `error.code`.
 */
export function TokenServiceAccountModal(props: TokenServiceAccountModalProps) {
  if (!props.open) return null
  return (
    <TokenServiceAccountModalForm
      key={`${resourceScopeKey(resourceScopeFromOwner(props))}:${props.descriptor.providerId}:${props.credentialId ?? 'new'}`}
      {...props}
    />
  )
}

function TokenServiceAccountModalForm({
  open,
  onOpenChange,
  workspaceId,
  organizationId,
  descriptor,
  serviceName,
  serviceIcon: ServiceIcon,
  credentialId,
  initialDisplayName,
  initialDescription,
  onCreated,
}: TokenServiceAccountModalProps) {
  const t = useT()
  const [values, setValues] = useState<Partial<Record<TokenServiceAccountFieldId, string>>>({})
  const [displayName, setDisplayName] = useState(initialDisplayName ?? '')
  const [description, setDescription] = useState(initialDescription ?? '')
  const [error, setError] = useState<string | null>(null)

  const createCredential = useCreateScopedCredential()
  const updateCredential = useUpdateScopedCredential()

  const normalized = Object.fromEntries(
    descriptor.fields.map((field) => [field.id, normalizeField(field, values[field.id] ?? '')])
  ) as Record<TokenServiceAccountFieldId, string>
  const isPending = createCredential.isPending || updateCredential.isPending
  const isDisabled = descriptor.fields.some((field) => !normalized[field.id]) || isPending

  const hintFor = (field: TokenServiceAccountField, value: string): string | undefined => {
    if (!field.hintPattern || !field.hintMessage || value.length === 0) return undefined
    return field.hintPattern.test(value) ? undefined : field.hintMessage
  }

  const setValue = (id: TokenServiceAccountFieldId, value: string) => {
    setValues((current) => ({ ...current, [id]: value }))
    if (error) setError(null)
  }

  const handleSubmit = async () => {
    setError(null)
    if (isDisabled) return
    try {
      if (credentialId) {
        await updateCredential.mutateAsync({
          ...resourceScopeFields(resourceScopeFromOwner({ workspaceId, organizationId })),
          credentialId,
          ...normalized,
          displayName: displayName.trim() || undefined,
          description: description.trim() || undefined,
        })
        onCreated?.(credentialId)
      } else {
        const created = await createCredential.mutateAsync({
          ...resourceScopeFields(resourceScopeFromOwner({ workspaceId, organizationId })),
          type: 'service_account',
          providerId: descriptor.providerId,
          ...normalized,
          displayName: displayName.trim() || undefined,
          description: description.trim() || undefined,
        })
        onCreated?.(created.credential.id)
      }
      onOpenChange(false)
    } catch (err: unknown) {
      const code = isApiClientError(err) ? err.code : undefined
      setError(getTokenServiceAccountErrorMessage(descriptor, code))
      logger.error(`Failed to add ${descriptor.serviceLabel} service account credential`, err)
    }
  }

  return (
    <ChipModal
      open={open}
      onOpenChange={onOpenChange}
      srTitle={t('Add {service} {noun}', {
        service: serviceName,
        noun: t(descriptor.connectNoun),
      })}
    >
      <ChipModalHeader icon={withBrandIcon(ServiceIcon)} onClose={() => onOpenChange(false)}>
        {t('Add {service} {noun}', { service: serviceName, noun: t(descriptor.connectNoun) })}
      </ChipModalHeader>
      <ChipModalBody>
        {descriptor.fields.map((field) =>
          field.secret ? (
            <ChipModalField
              key={field.id}
              type='custom'
              title={t(field.label)}
              required
              hint={
                hintFor(field, normalized[field.id]) ??
                (descriptor.helpText ? t(descriptor.helpText) : undefined)
              }
            >
              <SecretInput
                value={values[field.id] ?? ''}
                onChange={(value) => setValue(field.id, value)}
                placeholder={field.placeholder}
                name={`${descriptor.providerId}_${field.id}`}
                autoComplete='new-password'
                autoCorrect='off'
                autoCapitalize='off'
                data-lpignore='true'
                data-form-type='other'
              />
            </ChipModalField>
          ) : (
            <ChipModalField
              key={field.id}
              type='input'
              title={t(field.label)}
              value={values[field.id] ?? ''}
              onChange={(value) => setValue(field.id, value)}
              placeholder={field.placeholder}
              autoComplete='off'
              required
              error={hintFor(field, normalized[field.id])}
            />
          )
        )}

        <ChipModalField
          type='input'
          title={t('Display name')}
          value={displayName}
          onChange={setDisplayName}
          placeholder={t('Defaults to the {service} account name', {
            service: descriptor.serviceLabel,
          })}
          autoComplete='off'
        />

        <ChipModalField
          type='textarea'
          title={t('Description')}
          value={description}
          onChange={setDescription}
          placeholder={t('Optional description')}
          maxLength={500}
          minHeight={80}
        />

        <ChipModalError>{error}</ChipModalError>
      </ChipModalBody>
      <ChipModalFooter
        onCancel={() => onOpenChange(false)}
        secondaryActions={[
          {
            label: t('Setup guide'),
            onClick: () => openDocs(descriptor.docsUrl),
          },
        ]}
        primaryAction={{
          label: isPending ? t('Adding...') : t('Add {noun}', { noun: t(descriptor.connectNoun) }),
          onClick: handleSubmit,
          disabled: isDisabled,
        }}
      />
    </ChipModal>
  )
}
